import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Topbar } from "../components/shell/Topbar";
import { Button } from "../components/ui/button";
import { trackEvent } from "../lib/analytics";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useLayoutContext } from "../layouts/DashboardLayout";
import { cn } from "@/packages/utils/cn";
import { slugify } from "@/packages/utils/slug";
import { Loader2 } from "lucide-react";

interface Team {
  id: string;
  referenceId: string;
  name: string;
  slug: string;
  isPersonal: boolean;
  role?: "owner" | "admin" | "member";
}

interface Project {
  id: string;
  referenceId: string;
  name: string;
  slug: string;
  teamId: string;
  description?: string | null;
  color?: string;
}

interface Invite {
  id: string;
  teamId: string;
  email: string;
  role: string;
  expiresAt: string;
  team: { id: string; name: string };
}

const PALETTE = ["#C8362C", "#3E7C5C", "#C7903A", "#8C7AB0", "#1B1F2A"];

function rolePill(role?: string) {
  if (!role) return null;
  const tone =
    role === "owner"
      ? "text-signal-red"
      : role === "admin"
        ? "text-signal-warn"
        : "text-signal-ok";
  return (
    <span className={`font-mono text-[10px] tracking-wider uppercase ${tone}`}>
      ● {role}
    </span>
  );
}

function ProjectCard({
  project,
  teamId,
  active,
}: {
  project: Project;
  teamId: string;
  active?: boolean;
}) {
  return (
    <Link
      to={`/teams/${teamId}/projects/${project.referenceId}`}
      className={cn(
        "group bg-card-paper border rounded-xl overflow-hidden transition-all hover:shadow-lg hover:-translate-y-px relative",
        active ? "border-ink shadow-md" : "border-rule",
      )}
    >
      <div
        className="h-1"
        style={{ background: project.color ?? "#C8362C" }}
        aria-hidden="true"
      />
      <div className="p-4">
        <div className="font-serif text-lg font-semibold tracking-tight mb-1">
          {project.name}
        </div>
        <div className="text-xs text-muted-foreground line-clamp-2 min-h-[2.5em]">
          {project.description ?? "No description."}
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="font-mono text-[10px] text-faint truncate">
            {project.slug}
          </span>
          <span className="font-mono text-[11px] text-signal-red font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
            Open →
          </span>
        </div>
      </div>
    </Link>
  );
}

export function TeamsAndProjectsPage() {
  const navigate = useNavigate();
  const params = useParams();
  const focusedTeamId = params.teamId;
  const [searchParams, setSearchParams] = useSearchParams();
  const { teams, projects, refreshProjects } = useLayoutContext();
  const [invites, setInvites] = useState<Invite[]>([]);
  const [showCreateProject, setShowCreateProject] = useState<string | null>(
    null,
  );
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [seedingTeamId, setSeedingTeamId] = useState<string | null>(null);
  const [seedError, setSeedError] = useState<string | null>(null);

  // Spawn a starter project with one example mock + one example webhook so
  // the user lands on something useful instead of a second empty room. Three
  // sequential calls; if one fails the partial project stays for the user to
  // delete — better than rolling back and showing a generic error.
  const seedExampleProject = async (teamId: string) => {
    setSeedingTeamId(teamId);
    setSeedError(null);
    try {
      const projectRes = await fetch(`/api/teams/${teamId}/projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: "Example project",
          description: "A sandbox to poke around. Delete me when done.",
          color: "#C8362C",
        }),
      });
      if (!projectRes.ok) {
        throw new Error(
          ((await projectRes.json()) as { error?: string }).error ??
            "Failed to create project",
        );
      }
      const project = (await projectRes.json()) as Project;

      await Promise.all([
        fetch(`/api/projects/${project.referenceId}/mocks`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: "Hello world",
            description: "A 200 OK with a JSON body. Try editing the response.",
            method: "GET",
            endpoint: "/hello",
            statusCode: 200,
            responseBody: '{"hello":"world","tip":"edit me"}',
            responseHeaders: { "Content-Type": "application/json" },
            enabled: true,
          }),
        }),
        fetch(`/api/projects/${project.referenceId}/webhooks`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: "Example inbox",
            description:
              "Send a curl request to this webhook's URL to see it captured live.",
          }),
        }),
      ]);

      trackEvent("seed_example_project", { teamId });
      refreshProjects();
      navigate(`/teams/${teamId}/projects/${project.referenceId}`);
    } catch (err) {
      setSeedError(
        err instanceof Error
          ? err.message
          : "Couldn't seed the example project",
      );
    } finally {
      setSeedingTeamId(null);
    }
  };

  useEffect(() => {
    fetch("/api/invites/pending")
      .then((r) => (r.ok ? r.json() : []))
      .then((d: Invite[]) => setInvites(d ?? []))
      .catch(() => setInvites([]));
  }, []);

  useEffect(() => {
    if (searchParams.get("new") === "team") {
      setShowCreateTeam(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    if (searchParams.get("new") === "project" && focusedTeamId) {
      setShowCreateProject(focusedTeamId);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, focusedTeamId]);

  const acceptInvite = async (token: string) => {
    const res = await fetch("/api/invites/accept", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    if (res.ok) {
      refreshProjects();
      setInvites((prev) => prev.filter((i) => i.id !== token));
      fetch("/api/invites/pending")
        .then((r) => r.json())
        .then(setInvites);
    }
  };

  const declineInvite = async (id: string) => {
    setInvites((prev) => prev.filter((i) => i.id !== id));
  };

  const orderedTeams = useMemo(() => {
    if (!focusedTeamId) return teams;
    const focused = teams.find((t) => t.id === focusedTeamId);
    if (!focused) return teams;
    return [focused, ...teams.filter((t) => t.id !== focusedTeamId)];
  }, [teams, focusedTeamId]);

  return (
    <>
      <Topbar
        eyebrow="Teams"
        title="Your teams & projects"
        crumbs={[{ label: "Teams" }]}
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => setShowCreateTeam(true)}>
              + New team
            </Button>
            {orderedTeams[0] && (
              <Button
                size="sm"
                onClick={() => setShowCreateProject(orderedTeams[0]!.id)}
              >
                + New project
              </Button>
            )}
          </>
        }
      />

      <div className="px-4 sm:px-8 py-6 space-y-10">
        {orderedTeams.map((team) => {
          const teamProjects = projects[team.id] ?? [];
          return (
            <section key={team.id} className="space-y-3">
              <div className="flex items-baseline justify-between">
                <div className="flex items-baseline gap-3">
                  <span className="text-eyebrow">
                    Team {team.isPersonal ? "· personal" : ""}
                  </span>
                  {rolePill(team.role)}
                </div>
                <Link
                  to={`/teams/${team.id}/settings`}
                  className="font-mono text-[11px] text-faint hover:text-foreground"
                >
                  Manage members →
                </Link>
              </div>

              <div className="bg-card-paper border border-rule rounded-xl p-5 grid grid-cols-[auto_1fr_auto] items-center gap-6">
                <div
                  className="size-12 rounded-lg bg-ink text-paper flex items-center justify-center text-xl font-bold"
                  aria-hidden="true"
                >
                  {team.name.slice(0, 1).toUpperCase()}
                </div>
                <div>
                  <h3 className="font-sans font-semibold text-xl tracking-tight">
                    {team.name}
                  </h3>
                  <div className="text-sm text-muted-foreground">
                    {teamProjects.length}{" "}
                    {teamProjects.length === 1 ? "project" : "projects"}
                    {team.isPersonal ? " · personal team" : ""}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Link to={`/teams/${team.id}/settings`}>
                    <Button variant="ghost" size="sm">
                      Settings
                    </Button>
                  </Link>
                  {(team.role === "admin" || team.role === "owner") && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowCreateProject(team.id)}
                    >
                      + Project
                    </Button>
                  )}
                </div>
              </div>

              {teamProjects.length === 0 ? (
                <div className="bg-panel/40 border border-dashed border-rule rounded-xl p-6 text-center space-y-3">
                  <div className="text-sm text-muted-foreground">
                    No projects yet.{" "}
                    <span className="font-serif italic">
                      Want to peek without setting things up?
                    </span>
                  </div>
                  {(team.role === "admin" || team.role === "owner") && (
                    <div className="flex flex-wrap items-center justify-center gap-3">
                      <Button
                        size="sm"
                        onClick={() => seedExampleProject(team.id)}
                        disabled={seedingTeamId === team.id}
                      >
                        {seedingTeamId === team.id ? (
                          <>
                            <Loader2 className="size-4 animate-spin" />
                            Seeding…
                          </>
                        ) : (
                          "Seed example project →"
                        )}
                      </Button>
                      <button
                        onClick={() => setShowCreateProject(team.id)}
                        className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4 decoration-rule"
                        disabled={seedingTeamId === team.id}
                      >
                        Or create a blank one
                      </button>
                    </div>
                  )}
                  {seedError && seedingTeamId === null && (
                    <div className="text-xs text-signal-red">{seedError}</div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {teamProjects.map((p) => (
                    <ProjectCard key={p.id} project={p} teamId={team.id} />
                  ))}
                </div>
              )}
            </section>
          );
        })}

        {invites.length > 0 && (
          <section>
            <div className="text-eyebrow mb-3">
              Pending invites · {invites.length}
            </div>
            <div className="space-y-2">
              {invites.map((inv) => (
                <div
                  key={inv.id}
                  className="bg-signal-warn-soft border border-signal-warn/40 rounded-lg p-4 flex items-center gap-4"
                >
                  <span className="size-8 rounded bg-signal-warn text-paper flex items-center justify-center font-bold">
                    {inv.team.name.slice(0, 1).toUpperCase()}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">
                      {inv.team.name} invited you
                    </div>
                    <div className="text-xs text-muted-foreground">
                      As <b>{inv.role}</b> · expires{" "}
                      {new Date(inv.expiresAt).toLocaleDateString()}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => declineInvite(inv.id)}
                  >
                    Decline
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => acceptInvite(inv.id)}
                  >
                    Accept →
                  </Button>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      <CreateTeamDialog
        open={showCreateTeam}
        onOpenChange={setShowCreateTeam}
        onCreated={(team) => {
          refreshProjects();
          navigate(`/teams/${team.id}`);
        }}
      />
      <CreateProjectDialog
        teamId={showCreateProject}
        onOpenChange={(open) => !open && setShowCreateProject(null)}
        onCreated={(project) => {
          refreshProjects();
          navigate(`/teams/${project.teamId}/projects/${project.referenceId}`);
        }}
      />
    </>
  );
}

function CreateTeamDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: (team: Team) => void;
}) {
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch("/api/teams", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) throw new Error((await res.json()).error ?? "Failed");
      const team = await res.json();
      onOpenChange(false);
      setName("");
      onCreated(team);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create team");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New team</DialogTitle>
          <DialogDescription>
            Teams group projects and members. You'll be the owner.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="team-name">Name</Label>
            <Input
              id="team-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Engineering"
            />
          </div>
          {error && (
            <div className="text-sm text-signal-red">{error}</div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting || !name}>
            Create team
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateProjectDialog({
  teamId,
  onOpenChange,
  onCreated,
}: {
  teamId: string | null;
  onOpenChange: (o: boolean) => void;
  onCreated: (project: Project) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState(PALETTE[0]!);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const slugPreview = slugify(name);

  useEffect(() => {
    if (!teamId) {
      setName("");
      setDescription("");
      setError(null);
    }
  }, [teamId]);

  const submit = async () => {
    if (!teamId) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/teams/${teamId}/projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          description,
          color,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).error ?? "Failed");
      const project = await res.json();
      onOpenChange(false);
      onCreated(project);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={Boolean(teamId)}
      onOpenChange={(o) => !submitting && onOpenChange(o)}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New project</DialogTitle>
          <DialogDescription>
            A project owns mocks, webhooks, flags and releases.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="project-name">Name</Label>
            <Input
              id="project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Checkout 2.0"
              disabled={submitting}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Slug</Label>
            <div className="flex h-9 items-center rounded-md border border-input bg-muted px-3 text-sm font-mono text-muted-foreground">
              {slugPreview || (
                <span className="italic">auto-generated from the name</span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Auto-generated from the name. Used in subdomain URLs.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="project-desc">Description</Label>
            <Textarea
              id="project-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              disabled={submitting}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Color</Label>
            <div className="flex gap-2">
              {PALETTE.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  disabled={submitting}
                  className={cn(
                    "size-8 rounded-md border-2 disabled:opacity-50 disabled:cursor-not-allowed",
                    color === c ? "border-foreground" : "border-transparent",
                  )}
                  style={{ background: c }}
                  aria-label={`Color ${c}`}
                />
              ))}
            </div>
          </div>
          {error && (
            <div className="text-sm text-signal-red">{error}</div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting || !name.trim()}>
            {submitting ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                Creating…
              </>
            ) : (
              "Create project"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
